import { useContext, useState, useEffect } from 'react'
import { baseURL } from '../config'
import axios from 'axios'
import AuthContext from '../Context/AuthContext'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router';
import { MachineOrderFormValidation, getOrderErrorScrollTarget } from '../utils/MachineOrderFormValidation'
import UseEditTemplate from '../Components/UseEditTemplate'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChanges'

function MachineOrderEdit() {
  const [fetchingTemplateDetails, setFetchingTemplateDetails] = useState(true)
  const [templateDetails, setTemplateDetails] = useState([])
  const [originalTemplateDetails, setOriginalTemplateDetails] = useState([])
  const { token } = useContext(AuthContext);
  const [templateModelDesc, setTemplateModelDesc] = useState('');
  const [selectedAssembly, setSelectedAssembly] = useState([])
  const [originalSelectedAssembly, setOriginalSelectedAssembly] = useState([])
  // The order's main assembly (its first assembly). The lists above hold the sub assemblies only.
  const [mainAssembly, setMainAssembly] = useState(null)
  const [originalMainAssembly, setOriginalMainAssembly] = useState(null)
  // Model description of the order's template (found by Model #): used when a replaced main has none
  const [templateModelDescription, setTemplateModelDescription] = useState(null)
  const [originalCustomerData, setOriginalCustomerData] = useState({
    account_number: '',
    name: '',
    company_name: '',
    primary_contact: '',
    primary_email: '',
    accounting_contact: '',
    accounting_email: '',
    bill_to: '',
    ship_to: '',
    others: '',
    shipment_date: '',
  })
  const [originalModsData, setOriginalModsData] = useState({
    sales_order_number: '',
    serial_number: '',
    customer_po: '',
  })
  const { assembly_id } = useParams();
  const [assignedQR, setAssignedQR] = useState()
  const [qr, setQR] = useState('')
  const [accountNumberOptions, setAccountNumberOptions] = useState([])
  const [savingMachineOrders, setSavingMachineOrders] = useState(false)
  const navigate = useNavigate()
  const [mods_data, setMods_data] = useState({
    sales_order_number: '',
    serial_number: '',
    customer_po: '',
  });
  const [customer_data, setCustomer_data] = useState({
    account_number: '',
    name: '',
    company_name: '',
    primary_contact: '',
    primary_email: '',
    accounting_contact: '',
    accounting_email: '',
    bill_to: '',
    ship_to: '',
    others: '',
    shipment_date: '',
  });
  const [validateFields, setValidateFields] = useState();

  // Everything the user can change on this page, compared against how it looked once the order loaded
  const { allowNavigation } = useUnsavedChangesGuard({
    mods_data,
    customer_data,
    qr: qr || '',
    selectedAssembly,
    mainAssemblyId: mainAssembly?._id || '',
    subAssemblyIds: Object.values(templateDetails).flat().map((item) => item._id),
  }, !fetchingTemplateDetails)

  const saveEditMachineOrder = () => {
    // Sub assemblies belong to a main assembly: an order without one has to get one first
    if (!templateModelDesc?.is_buyout && !mainAssembly && selectedAssembly.length > 0) {
      toast.error('Select a main assembly before saving sub assemblies')
      return
    }
    let accountNumberError
    if (!originalCustomerData['account_number']) {
      accountNumberError = false
    } else if (customer_data['account_number'] && accountNumberOptions.includes(customer_data['account_number'])) {
      accountNumberError = false
    } else {
      accountNumberError = true
    }
    const [isAnyEmptyFields, validateFieldsCopy] = MachineOrderFormValidation(qr, mods_data, customer_data, validateFields, accountNumberError, !templateModelDesc?.is_buyout);
    setValidateFields(validateFieldsCopy)
    if (isAnyEmptyFields) {
      const tableElement = document.getElementById(getOrderErrorScrollTarget(validateFieldsCopy));
      tableElement.scrollIntoView({ behavior: "smooth" });
    } else {
      const createMODS = async () => {
        try {
          setSavingMachineOrders(true)
          let modifiedExistingCustomer = null;

          if (originalCustomerData['account_number'] && (originalCustomerData['account_number'] !== customer_data['account_number'])) {
            modifiedExistingCustomer = customer_data['_id'];
            delete customer_data._id;
          }

          // Construct the payload dynamically. The main assembly is saved first, followed by the
          // selected sub assemblies (buyout orders have no main assembly).
          const payload = {
            mods_data: mods_data,
            customer_data: customer_data,
            assembly_list: selectedAssembly,
            ...(!templateModelDesc?.is_buyout && mainAssembly ? { main_assembly: mainAssembly._id } : {}),
            ...(modifiedExistingCustomer ? { customer_id: modifiedExistingCustomer } : {}),
          }

          const response = await axios.put(`${baseURL}/mods/modifyMods/${assembly_id}`,
            payload,
            {headers: {
              "ngrok-skip-browser-warning":"any",
              "x-access-token": token
              }
            }
          );
          if (response.status === 200){
            toast.success('Machine Order Created Successfully')
            allowNavigation()
            navigate('/machine_orders')
          }
        } catch (error) {
          toast.error('Something Went Wrong, Template Not Updated')
        } finally {
          setSavingMachineOrders(false)
        }
      }
      createMODS()
    }
  }

  useEffect(() => {
    // Only the latest request is applied (React StrictMode runs this effect twice in dev), so a
    // slower duplicate response can't overwrite edits made in the meantime.
    let ignore = false;
    const fetchData = async () => {
      try {
        setFetchingTemplateDetails(true);
        const response = await axios.get(`${baseURL}/mods/getModsById/${assembly_id}`, {
          headers: {
            "ngrok-skip-browser-warning": "any",
            "Content-Type": "multipart/form-data",
            "x-access-token": token
          },
          params: {
            mods_id: assembly_id
          }
        });
        if (ignore) return;
        // delete response.data.data.customerData._id;
        delete response.data.data.customerData.__v;
        delete response.data.data.customerData.created_at;
        delete response.data.data.customerData.updated_at;
        setCustomer_data(response.data.data.customerData);
        // Buyout orders have no model details, so they aren't part of the form or the save
        if (response.data.data.templateData.is_buyout) {
          delete response.data.data.modsData.model_number
          delete response.data.data.modsData.model_description
        }
        delete response.data.data.modsData.created_at
        setMods_data(response.data.data.modsData);
        setOriginalCustomerData(response.data.data.customerData)
        setOriginalModsData(response.data.data.modsData)
        setAssignedQR(response.data.data.qrData)
        setQR(response.data.data.qrData._id);
        // The main assembly is the order's first assembly (none for buyout orders); the sub
        // assemblies are the rest, still grouped by type.
        const mainId = response.data.data.mainAssemblyId
        let main = null
        const subs = {}
        Object.entries(response.data.data.assemblyData).forEach(([type, items]) => {
          const rest = items.filter((item) => {
            if (mainId && item._id === mainId) {
              main = item
              return false
            }
            return true
          })
          if (rest.length > 0) subs[type] = rest
        })
        setMainAssembly(main)
        setOriginalMainAssembly(main)
        setTemplateModelDescription(response.data.data.templateModelDescription || null)
        setTemplateDetails(subs);
        setOriginalTemplateDetails(subs);
        setTemplateModelDesc(response.data.data.templateData);
        const ids = Object.keys(subs).flatMap(key => (subs[key]).map((item) => (item._id)));
        setSelectedAssembly(ids)
        setOriginalSelectedAssembly(ids)
      } catch (error) {
        console.error('Error:', error);
      } finally {
        if (!ignore) setFetchingTemplateDetails(false);
      }
    };

    fetchData();
    return () => {
      ignore = true;
    };
  }, [assembly_id, token]);

  useEffect(() => {
    if (!fetchingTemplateDetails) {
        setValidateFields(prev => {
            const result = MachineOrderFormValidation(qr, mods_data, customer_data, prev, undefined, !templateModelDesc?.is_buyout);
            return result[1];
        });
    }
  }, [qr, mods_data, customer_data, fetchingTemplateDetails, templateModelDesc]);

  return (
    <UseEditTemplate
      savingMachineOrders = {savingMachineOrders}
      originalSelectedAssembly = {originalSelectedAssembly}
      templateModelDesc = {templateModelDesc}
      templateDetails={templateDetails}
      setTemplateDetails={setTemplateDetails}
      originalTemplateDetails = {originalTemplateDetails}
      fetchingTemplateDetails = {fetchingTemplateDetails}
      accountNumberOptions = {accountNumberOptions}
      setAccountNumberOptions = {setAccountNumberOptions}
      mods_data = {mods_data}
      setMods_data = {setMods_data}
      customer_data = {customer_data}
      setCustomer_data = {setCustomer_data}
      assignedQR = {assignedQR}
      setAssignedQR = {setAssignedQR}
      validateFields = {validateFields}
      saveChanges = {saveEditMachineOrder}
      selectedAssembly = {selectedAssembly}
      setSelectedAssembly = {setSelectedAssembly}
      originalCustomerData={originalCustomerData}
      originalModsData={originalModsData}
      qr = {qr}
      setQR = {setQR}
      mainAssembly={mainAssembly}
      setMainAssembly={setMainAssembly}
      originalMainAssembly={originalMainAssembly}
      fallbackModelDescription={templateModelDescription}
      editOrder = {true}
    />
  )
}

export default MachineOrderEdit